import React from "react";
import { GoogleMap, LoadScript, MarkerClusterer, Marker, InfoWindow } from "@react-google-maps/api";
import { MarkerIcon } from "../constants/markers";
import { isPointWithinUSBounds } from "../utils/functions";
import AppSettings from "../config";
import SiteContext from "../contexts";
import queryString from "query-string";

declare global {
  interface Window {
    google: any;
  }
}

interface CaseMapState {
  center: { lat: number; lng: number };
  zoom: number;
  caseMarkers: any;
  map: any;
  mapChangedByUser: boolean;
}

interface CaseMapProps {
  cases: Array<Record<string, unknown>>;
  caseHovered?: any;
  caseSelected?: any;
  onMarkerHovered?: { (caseObj: any): void };
  onMarkerClicked?: { (caseObj: any): void };
  cluster: boolean;
  map?: any;
}

class CaseMap extends React.Component<any> {
  readonly state: CaseMapState = {
    caseMarkers: [],
    center: { lat: 0, lng: 0 },
    zoom: 7,
    map: null,
    mapChangedByUser: false,
  };

  static contextType = SiteContext;
  static defaultProps: CaseMapProps = {
    cases: [],
    caseHovered: null,
    caseSelected: null,
    cluster: false,
  };

  static getDerivedStateFromProps(props: CaseMapProps, state: CaseMapState) {
    const { center } = state;
    if (!window.google) {
      return null;
    }
    if (center.lat % 180 !== 0 || center.lng % 180 !== 0) {
      return null;
    }

    const bounds = new window.google.maps.LatLngBounds();
    props.cases.forEach((caseObj: any) => {
      const lat = parseFloat(caseObj.y), lon = parseFloat(caseObj.x);
      if (!isPointWithinUSBounds(lat, lon)) return;
      bounds.extend({ lat: lat, lng: lon });
    });

    state.map.fitBounds(bounds);
    return {
      center: { lat: bounds.getCenter().lat(), lng: bounds.getCenter().lng() },
    };
  }

  editCaseLink(caseObj: any): string {
    if (caseObj.record.object === "Case") {
      return `${AppSettings.GOGOV_WEB_URL}/${this.context.site}/ceeditcase.php?did=${caseObj.record.displayId}&cmd=edit`;
    }
    return `${AppSettings.GOGOV_WEB_URL}/${this.context.site}/editcase.php?did=${caseObj.record.displayId}&cmd=edit`;
  }

  markerIconForCase(caseObj: any) {
    const { caseHovered } = this.props;
    const hovered = caseHovered && caseHovered.record.caseid === caseObj.record.caseid;
    const status = caseObj.record.status;
    return hovered ? MarkerIcon.blue : status === "Closed" ? MarkerIcon.green : MarkerIcon.red;
  }

  onCenterChanged = () => {
    const center = this.state.map.getCenter();
    this.setState({ mapChangedByUser: true, center: { lat: center.lat(), lng: center.lng() } });
  };

  onZoomChanged = () => {
    if (!this.state.map) {
      return;
    }
    const zoom = this.state.map.getZoom();
    if (zoom === this.state.zoom) {
      return;
    }
    this.setState({ mapChangedByUser: true, zoom });
  };

  renderMarkers = (clusterer: any) => {
    return this.props.cases.map((caseObj: any) => this.renderMarker(caseObj, clusterer));
  };

  renderMarker = (caseObj: any, clusterer: any) => {
    const { caseHovered } = this.props;
    const icon = this.markerIconForCase(caseObj);
    const hovered = caseHovered && caseHovered.record.caseid === caseObj.record.caseid;
    const zIndex = hovered ? 100 : 1;
    const onClick = () => {
      this.setState({ mapChangedByUser: true });
      if (this.props.onMarkerClicked) {
        this.props.onMarkerClicked(caseObj);
      }
    };
    const onMouseIn = () => {
      if (this.props.onMarkerHovered) {
        this.props.onMarkerHovered(caseObj);
      }
    };
    const onMouseOut = () => {
      if (this.props.onMarkerHovered) {
        this.props.onMarkerHovered(null);
      }
    };

    return (
      <Marker
        key={caseObj.record.caseid}
        icon={icon}
        clusterer={clusterer}
        onClick={onClick}
        onMouseOver={onMouseIn}
        onMouseOut={onMouseOut}
        zIndex={zIndex}
        position={{ lat: parseFloat(caseObj.y), lng: parseFloat(caseObj.x) }}
      ></Marker>
    );
  };

  renderInfoWindow() {
    if (!this.props.caseSelected) {
      return;
    }

    const {
      record: { caseid, displayId, location, status, object, topic, dateEntered, assignedTo },
    } = this.props.caseSelected;
    const position = { lat: parseFloat(this.props.caseSelected.y), lng: parseFloat(this.props.caseSelected.x) };
    const editCaseLink = this.editCaseLink(this.props.caseSelected);
    const directionsLink = `https://www.google.com/maps/dir/?${queryString.stringify({
      api: 1,
      destination: location,
      travelmode: "car",
    })}`;
    const options = {
      pixelOffset: {
        height: -32,
        width: 0,
        equals: () => true,
      },
    };

    const onCloseClick = () => {
      if (this.props.onMarkerClicked) {
        this.props.onMarkerClicked(null);
      }
    };

    return (
      <InfoWindow key={caseid} position={position} options={options} onCloseClick={onCloseClick}>
        <div className='CaseInfoWindow'>
          <h1>
            <a href={editCaseLink} target={"_blank"}>
              {object} #{displayId}
            </a>
          </h1>
          <p>
            <b>Address:</b> {location}
          </p>
          <p>
            <b>Status:</b> {status}
          </p>
          <p>
            <b>Topic:</b> {topic}
          </p>
          <p>
            <b>Reported:</b> {dateEntered}
          </p>
          <p>
            <b>Assignee:</b> {assignedTo}
          </p>
          <p>
            <a href={directionsLink} target={"_blank"}>
              Driving Directions
            </a>
          </p>
        </div>
      </InfoWindow>
    );
  }

  renderClusters() {
    const onClusterClick = () => {
      this.setState({ mapChangedByUser: true });
      if (this.props.onMarkerClicked) {
        this.props.onMarkerClicked(null);
      }
    };

    return (
      <MarkerClusterer
        onClick={onClusterClick}
        options={{
          imagePath: "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
        }}
      >
        {(clusterer) => this.renderMarkers(clusterer)}
      </MarkerClusterer>
    );
  }

  render() {
    const { center, zoom } = this.state;
    const { cluster } = this.props;

    return (
      <LoadScript googleMapsApiKey={AppSettings.GOOGLE_MAP_KEY}>
        <GoogleMap
          id='dashboard-map'
          zoom={zoom}
          center={center}
          clickableIcons={false}
          onDragStart={this.onCenterChanged}
          onZoomChanged={this.onZoomChanged}
          onLoad={(map) => {
            this.setState({ map: map });
          }}
        >
          {cluster ? this.renderClusters() : this.renderMarkers(null)}
          {this.renderInfoWindow()}
        </GoogleMap>
      </LoadScript>
    );
  }
}

export default CaseMap;
